import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getAiProviderSetting,
  setAiProviderSetting,
  type AiProvider,
} from "@/lib/admin.ai-provider.functions";

export const Route = createFileRoute("/admin/ai-provider")({
  head: () => ({
    meta: [
      { title: "AI provider — Tiger's Eye Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AiProviderAdmin,
});

function AiProviderAdmin() {
  const getFn = useServerFn(getAiProviderSetting);
  const setFn = useServerFn(setAiProviderSetting);
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["admin", "ai-provider"],
    queryFn: () => getFn(),
  });

  const [provider, setProvider] = useState<AiProvider>("lovable");
  const [xaiModel, setXaiModel] = useState("grok-4-latest");
  const [hydrated, setHydrated] = useState(false);

  if (q.data && !hydrated) {
    setProvider(q.data.provider);
    setXaiModel(q.data.xai_model);
    setHydrated(true);
  }

  const mut = useMutation({
    mutationFn: (input: { provider: AiProvider; xai_model: string }) =>
      setFn({ data: input }),
    onSuccess: (data) => {
      qc.setQueryData(["admin", "ai-provider"], data);
      toast.success(
        data.provider === "xai"
          ? `Switched to x.ai (${data.xai_model}).`
          : "Switched to Lovable AI.",
      );
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : "Could not save provider.");
    },
  });

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <p className="overline">Test environment</p>
        <h2 className="font-serif text-3xl text-primary mt-1">AI provider</h2>
        <p className="text-sm text-ink-secondary mt-2">
          Choose which model gateway powers Tiger&apos;s Eye critiques. The change
          applies to all server-side critique runs immediately. The Lovable AI
          gateway is the default; x.ai (Grok) is enabled here for comparison.
        </p>
      </div>

      {q.isLoading ? (
        <p className="text-ink-tertiary font-serif">Loading current setting…</p>
      ) : q.isError ? (
        <p className="text-destructive">
          {q.error instanceof Error ? q.error.message : "Failed to load setting."}
        </p>
      ) : (
        <form
          className="space-y-6 border border-border bg-card p-6"
          onSubmit={(e) => {
            e.preventDefault();
            mut.mutate({ provider, xai_model: xaiModel.trim() });
          }}
        >
          <fieldset className="space-y-3">
            <legend className="overline">Provider</legend>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="provider"
                value="lovable"
                checked={provider === "lovable"}
                onChange={() => setProvider("lovable")}
                className="mt-1"
              />
              <span>
                <span className="font-medium text-primary">Lovable AI</span>
                <span className="block text-sm text-ink-tertiary">
                  Default. Routes through the Lovable AI gateway (Gemini / GPT
                  family). No per-request key required.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="provider"
                value="xai"
                checked={provider === "xai"}
                onChange={() => setProvider("xai")}
                className="mt-1"
              />
              <span>
                <span className="font-medium text-primary">x.ai (Grok)</span>
                <span className="block text-sm text-ink-tertiary">
                  Uses the <span className="font-mono">XAI_API_KEY</span> secret
                  against <span className="font-mono">api.x.ai</span>. Pick a model
                  available to that key.
                </span>
              </span>
            </label>
          </fieldset>

          <div className="space-y-2">
            <label
              htmlFor="xai-model"
              className="block text-sm font-medium text-ink-secondary"
            >
              x.ai model id
            </label>
            <input
              id="xai-model"
              type="text"
              value={xaiModel}
              onChange={(e) => setXaiModel(e.target.value)}
              disabled={provider !== "xai"}
              className="w-full max-w-md rounded-md border border-input bg-background px-3 py-2 text-sm font-mono text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
              placeholder="grok-4-latest"
            />
            <p className="text-xs text-ink-tertiary">
              Examples: <span className="font-mono">grok-4-latest</span>,{" "}
              <span className="font-mono">grok-4-fast-reasoning</span>,{" "}
              <span className="font-mono">grok-3</span>. Only applies when x.ai is
              selected.
            </p>
          </div>

          <div className="flex items-center gap-3 pt-2 border-t border-border">
            <button
              type="submit"
              disabled={mut.isPending}
              className="px-4 py-2 bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {mut.isPending ? "Saving…" : "Save provider"}
            </button>
            {q.data && (
              <span className="text-xs text-ink-tertiary">
                Currently active:{" "}
                <span className="font-mono">
                  {q.data.provider === "xai"
                    ? `x.ai · ${q.data.xai_model}`
                    : "lovable"}
                </span>
              </span>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
